import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Check, Paperclip } from "lucide-react";
import Attachments from "@/components/Attachments";

const COLUMNS = [
  { key: "todo", label: "To Do" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Done" },
];
const PRIORITY = {
  urgent: "bg-destructive text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-slate-200 text-slate-700",
  low: "bg-slate-100 text-slate-500",
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [attachTask, setAttachTask] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", project_id: "none", due_date: "" });

  const load = () => api.get("/tasks").then((r) => setTasks(r.data));
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const create = async () => {
    if (!form.title.trim()) return;
    const payload = { ...form, project_id: form.project_id === "none" ? null : form.project_id, due_date: form.due_date || null };
    await api.post("/tasks", payload);
    setOpen(false);
    setForm({ title: "", description: "", priority: "medium", project_id: "none", due_date: "" });
    toast.success("Task created");
    load();
  };

  const move = async (task, status) => {
    await api.put(`/tasks/${task.id}`, { status });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    load();
  };

  const projName = (id) => projects.find((p) => p.id === id)?.name;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono text-slate-500">Execution</div>
          <h1 className="text-4xl font-black tracking-tighter">Tasks</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="new-task-button">
              <Plus size={16} className="mr-2" /> New Task
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Task title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="rounded-sm" data-testid="task-title-input" />
              <Textarea placeholder="Details (optional)" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["urgent", "high", "medium", "low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="task-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="save-task-button">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {COLUMNS.map((col) => {
          const items = tasks.filter((t) => t.status === col.key);
          return (
            <div key={col.key} data-testid={`column-${col.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="label-mono text-slate-500">{col.label}</span>
                <span className="label-mono text-slate-400">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((t) => (
                  <Card key={t.id} className="rounded-sm border-border shadow-none p-3.5 group hover:border-slate-400 transition-colors" data-testid={`task-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className={`label-mono px-1.5 py-0.5 rounded-sm ${PRIORITY[t.priority]}`}>{t.priority}</span>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setAttachTask(t)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-950 transition-opacity" data-testid={`attach-task-${t.id}`}>
                          <Paperclip size={14} />
                        </button>
                        <button onClick={() => remove(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive transition-opacity" data-testid={`delete-task-${t.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className={`text-sm font-medium mt-2 ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</div>
                    {t.project_id && <div className="label-mono text-ai mt-1">{projName(t.project_id)}</div>}
                    {t.due_date && <div className="label-mono text-slate-400 mt-1">Due {t.due_date.slice(0, 10)}</div>}
                    <div className="flex gap-1.5 mt-3">
                      {COLUMNS.filter((c) => c.key !== t.status).map((c) => (
                        <button key={c.key} onClick={() => move(t, c.key)}
                          className="label-mono text-slate-500 border border-border rounded-sm px-2 py-1 hover:border-slate-950 hover:text-slate-950 transition-colors"
                          data-testid={`move-${t.id}-${c.key}`}>
                          {c.key === "done" ? <Check size={11} /> : "→ " + c.label}
                        </button>
                      ))}
                    </div>
                  </Card>
                ))}
                {items.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">Empty</p>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!attachTask} onOpenChange={(v) => !v && setAttachTask(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="truncate">{attachTask?.title}</DialogTitle></DialogHeader>
          {attachTask && <Attachments parentType="task" parentId={attachTask.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
